import React, { useState, useEffect, useContext } from "react";
import DatatableComponent from "../../helpers/DatatableComponent";
import {
  Button,
  Snackbar,
  Alert,
  Paper,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
} from "@mui/material";
import axios from "axios";
import SocketContext from "helpers/SocketContext";
import StatusLayout from "../../components/Status";
import moment from "moment";
import { connect } from "react-redux";
import { mapStateToProps } from "../../store/helpers/mapState";
import { mapDispatchToProps } from "../../store/helpers/mapDispatch";

function IncomingVehicleRequests({ SetSelectedMenu }) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [list, setList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const socket = useContext(SocketContext);

  // New state variables for driver assignment dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // State for deny dialog
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [denyComments, setDenyComments] = useState("");

  const handleCloseSuccessAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSuccessAlert(false);
  };

  // const handleOpenApproveDialog = async (request) => {
  //   setSelectedRequest(request);
  //   setOpenDialog(true);
  //   setLoadingDrivers(true);

  //   try {
  //     // Get available drivers for the time slot
  //     const response = await axios.get(
  //       `/api/vehicle-request/available-drivers`,
  //       {
  //         params: {
  //           from: request.from,
  //           to: request.to,
  //         },
  //       }
  //     );

  //     if (response.data && response.data.success) {
  //       setAvailableDrivers(response.data.data);
  //     } else {
  //       setError("Failed to fetch available drivers");
  //     }
  //   } catch (err) {
  //     setError("Error fetching available drivers");
  //   } finally {
  //     setLoadingDrivers(false);
  //   }
  // };

  // Add this function to fetch available drivers
  const fetchAvailableDrivers = async (from, to) => {
    try {
      setLoadingDrivers(true);
      const response = await axios.get("/api/driver/available", {
        params: { from, to },
      });

      if (response.data && response.data.success) {
        return response.data.data; // Ensure the correct property is returned
      } else {
        setError("Failed to fetch available drivers");
        return [];
      }
    } catch (error) {
      setError("Error fetching available drivers");
      return [];
    } finally {
      setLoadingDrivers(false);
    }
  };

  // Update the handleApprove function
  const handleApprove = async (requestId) => {
    // Open the driver assignment dialog
    const request = list.find((item) => item.id === requestId);
    if (!request) return;

    setSelectedRequest(request);

    // Fetch available drivers
    const drivers = await fetchAvailableDrivers(request.from, request.to);
    setAvailableDrivers(drivers);

    // Open the dialog
    setOpenDialog(true);
  };

  // Add a function to handle the final approval
  const handleConfirmApprove = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `/api/vehicle-request/approve/${selectedRequest.id}`,
        { driverId: selectedDriver || null }
      );

      if (response.data && response.data.success) {
        setSuccessMessage("Request approved successfully!");
        setOpenSuccessAlert(true);
        setCount((v) => v + 1);
        handleCloseDialog();
      } else {
        setError(response.data?.message || "Failed to approve request");
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to approve request");
    } finally {
      setLoading(false);
    }
  };

  // Add a function to close the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRequest(null);
    setSelectedDriver("");
  };

  const handleOpenDenyDialog = (requestId) => {
    setSelectedRequest(list.find((item) => item.id === requestId));
    setDenyDialogOpen(true);
  };

  const handleConfirmDeny = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `/api/vehicle-request/deny/${selectedRequest.id}`,
        { comments: denyComments } // Send denial comments to the backend
      );
      if (response.data && response.data.success) {
        setSuccessMessage("Request denied successfully!");
        setOpenSuccessAlert(true);
        setCount((v) => v + 1);
        setDenyDialogOpen(false);
        setDenyComments(""); // Reset comments
      } else {
        setError(response.data?.message || "Failed to deny request");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "An error occurred while denying the request"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = async (requestId) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `/api/vehicle-request/deny/${requestId}`
      );
      if (response.data && response.data.success) {
        setSuccessMessage("Request denied successfully!");
        setOpenSuccessAlert(true);
        setCount((v) => v + 1);

      } else {
        setError(response.data?.message || "Failed to deny request");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "An error occurred while denying the request"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (requestId) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `/api/vehicle-request/cancel/${requestId}`
      );
      if (response.data && response.data.success) {
        setSuccessMessage("Request canceled successfully!");
        setOpenSuccessAlert(true);
        setCount((v) => v + 1); // Trigger data refresh
      } else {
        setError(response.data?.message || "Failed to cancel request");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "An error occurred while canceling the request"
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate time remaining

  const columns = [
    {
      name: "Vehicle.plateNumber",
      label: "Plate Number",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || "N/A",
      },
    },
    {
      name: "Vehicle.model",
      label: "Model",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || "N/A",
      },
    },
    {
      name: "Requester.names",
      label: "Requested By",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || "Unknown",
      },
    },
    {
      name: "destination",
      label: "Destination",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || "N/A",
      },
    },
    {
      name: "dropOffFrom",
      label: "Drop-off From",
      options: {
        customBodyRender: (value) => {
          return value ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "blue" }}>
                <div>{moment(value).format("YYYY-MM-DD")}</div>

                {/* {moment(value).format("HH:mm")} */}
                {moment(value).utc().format("HH:mm")}
              </div>
            </div>
          ) : (
            "N/A"
          );
        },
      },
    },
    {
      name: "dropOffTo",
      label: "Drop-off To",
      options: {
        customBodyRender: (value) => {
          return value ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "blue" }}>
                <div>{moment(value).format("YYYY-MM-DD")}</div>

                {/* {moment(value).format("HH:mm")} */}
                {moment(value).utc().format("HH:mm")}
              </div>
            </div>
          ) : (
            "N/A"
          );
        },
      },
    },
    {
      name: "pickUpFrom",
      label: "Pick-up From",
      options: {
        customBodyRender: (value) => {
          return value ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "blue" }}>
                <div>{moment(value).format("YYYY-MM-DD")}</div>

                {/* {moment(value).format("HH:mm")} */}
                {moment(value).utc().format("HH:mm")}
              </div>
            </div>
          ) : (
            "N/A"
          );
        },
      },
    },
    // {moment(value).utc().format("HH:mm")}
    {
      name: "pickUpTo",
      label: "Pick-up From",
      options: {
        customBodyRender: (value) => {
          return value ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "blue" }}>
                <div>{moment(value).format("YYYY-MM-DD")}</div>

                {/* {moment(value).format("HH:mm")} */}
                {moment(value).utc().format("HH:mm")}
              </div>
            </div>
          ) : (
            "N/A"
          );
        },
      },
    },
    // {
    //   name: "reason",
    //   label: "Reason",
    //   options: {
    //     customBodyRender: (value) => value || "N/A",
    //   },
    // },
    {
      name: "Driver.names",
      label: "Assigned Driver",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const rowId = tableMeta.rowData[tableMeta.rowData.length - 1];
          const currentRow = list.find((item) => item.id === rowId);
          const status = currentRow?.status;

          if (status && status.toLowerCase() === "approved") {
            return value || "Self-drive";
          } else {
            return "Not assigned";
          }
        },
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <StatusLayout status={value || "unknown"} />
        ),
      },
    },
    {
      name: "id",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const currentRow = list.find((item) => item.id === value);
          const status = currentRow?.status;

          if (status && status.toLowerCase() === "pending") {
            return (
              <div className="flex space-x-2">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={() => handleApprove(value)}
                  size="small"
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={loading}
                  onClick={() => handleOpenDenyDialog(value)} // Open deny dialog
                  size="small"
                >
                  Deny
                </Button>
              </div>
            );
          } else if (status && status.toLowerCase() === "approved") {
            return (
              <Button
                variant="contained"
                color="warning"
                disabled={loading}
                onClick={() => handleCancel(value)}
                size="small"
              >
                Cancel
              </Button>
            );
          } else {
            return null;
          }
        },
      },
    },
  ];

  const statuses = [
    { label: "All", value: "all" },
    { label: "Approved", value: "Approved" },
    { label: "Denied", value: "Denied" },
    { label: "Cancelled", value: "Cancelled" },
    { label: "Pending", value: "Pending" },
  ];

  const processData = (data) => {
    // Filter data based on selected status
    const filteredData =
      selectedStatus === "all"
        ? data.data
        : data.data.filter((item) => item.status === selectedStatus);

    // Sort requests by status and then by creation time
    const sortedData = filteredData.sort((a, b) => {
      const statusOrder = { Pending: 1, Approved: 2, Denied: 3, Cancelled: 4 };
      const statusA = statusOrder[a.status] || 5;
      const statusB = statusOrder[b.status] || 5;

      if (statusA === statusB) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }

      return statusA - statusB;
    });

    setList(sortedData);
    return { data: sortedData };
  };

  return (
    <>
      <Paper elevation={0} className="w-full mb-12 px-4">
        <Snackbar
          open={openSuccessAlert}
          autoHideDuration={2000}
          onClose={handleCloseSuccessAlert}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSuccessAlert}
            severity="success"
            sx={{ width: "100%" }}
          >
            {successMessage}
          </Alert>
        </Snackbar>

        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        <DatatableComponent
          elevation={1}
          columns={columns}
          url={`/api/vehicle-request/list?status=${selectedStatus}&count=${count}`}
          title="Incoming Vehicle Requests"
          processData={processData}
          searchfield={true}
          search={false}
          viewColumns={false}
          statusFilter={{
            options: statuses,
            selectedStatus,
            onChange: (status) => setSelectedStatus(status),
          }}
        />
      </Paper>

      {/* Driver Assignment Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Assign Driver</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Requester:</strong>{" "}
                {selectedRequest.Requester?.names || "Unknown"}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Vehicle:</strong> {selectedRequest.Vehicle?.plateNumber}{" "}
                ({selectedRequest.Vehicle?.model})
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Drop-off From:</strong>{" "}
                {moment(selectedRequest.dropOffFrom).utc().format("YYYY-MM-DD HH:mm")}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Drop-off To:</strong>{" "}
                {moment(selectedRequest.dropOffTo).utc().format("YYYY-MM-DD HH:mm")}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Pick-Up From:</strong>{" "}
                {moment(selectedRequest.pickUpFrom).utc().format("YYYY-MM-DD HH:mm")}
              </Typography>
               <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Pick-Up To:</strong>{" "}
                {moment(selectedRequest.pickUpTo).utc().format("YYYY-MM-DD HH:mm")}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Destination:</strong> {selectedRequest.destination}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Reason:</strong> {selectedRequest.reason || "N/A"}
              </Typography>

              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel id="driver-select-label">Assign Driver</InputLabel>
                {loadingDrivers ? (
                  <CircularProgress size={24} sx={{ mt: 3 }} />
                ) : (
                  <Select
                    labelId="driver-select-label"
                    value={selectedDriver}
                    label="Assign Driver"
                    onChange={(e) => setSelectedDriver(e.target.value)}
                  >
                    {availableDrivers.map((driver) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.names}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmApprove}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deny Dialog */}
      <Dialog
        open={denyDialogOpen}
        onClose={() => setDenyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Deny Request</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please provide a reason for denying this request:
          </Typography>
          <TextField
            label="Comments"
            fullWidth
            multiline
            rows={4}
            value={denyComments}
            onChange={(e) => setDenyComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDeny}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Deny"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(IncomingVehicleRequests);
